/**
 * Configuration constants for neural network visualization
 */
export const NEURAL_CONFIG = {
  // Visual settings
  BACKGROUND_COLOR: 0x000411,
  FOG_COLOR: 0x000822,
  // Make fog start closer and end further for a more dramatic, visible effect
  FOG_NEAR: 30,
  FOG_FAR: 400,

  // Lighting
  AMBIENT_LIGHT: { color: 0x5588dd, intensity: 2.0 },
  DIRECTIONAL_LIGHT: { color: 0x77aaee, intensity: 1.8 },
  FILL_LIGHT: { color: 0x6699dd, intensity: 1.2 },

  // Camera
  FOV: 75,
  NEAR: 0.1,
  FAR: 1000,
  INITIAL_POSITION: { x: 0, y: 0, z: 100 },

  // Interaction
  MOUSE_TIMEOUT: 3000,
  CAMERA_SMOOTHING: 0.05,

  // Performance
  MAX_PIXEL_RATIO: 2,
  TOPOLOGY_UPDATE_INTERVAL: 60,
  GLOW_UPDATE_SAMPLES: 3,

  // Neural network
  NEURON_COLOR: 0x4477bb,
  ACTIVATION_DECAY: 0.9995,
  EPICENTER_PROBABILITY: 0.001,
  RIPPLE_SPEED: 8.0,
  CONNECTION_THRESHOLD: 0.7,

  // Geometry
  NEURON_SEGMENTS: { widthSegments: 16, heightSegments: 12 },
  TUBE_SEGMENTS: { radialSegments: 8, tubularSegments: 16 },
} as const

/**
 * Animation and timing utilities
 */
export class AnimationUtils {
  /**
   * Smooth step interpolation for smooth animations
   */
  static smoothStep(edge0: number, edge1: number, x: number): number {
    const t = Math.max(0, Math.min(1, (x - edge0) / (edge1 - edge0)))
    return t * t * (3 - 2 * t)
  }

  /**
   * Power curve for dramatic activation scaling
   */
  static powerCurve(x: number, power: number = 3): number {
    return Math.pow(Math.max(0, Math.min(1, x)), power)
  }

  /**
   * Generate pseudo-random value from neuron ID for consistent behavior
   */
  static randomFromId(id: number, seed: number = 0): number {
    const hash = ((id + seed) * 9301 + 49297) % 233280
    return hash / 233280
  }

  /**
   * Calculate distance-based ripple effect
   */
  static calculateRipple(
    distance: number,
    time: number,
    speed: number,
  ): number {
    const wavePosition = time * speed
    const rippleDistance = Math.abs(distance - wavePosition)
    return Math.max(0, 1 - rippleDistance / 20) * Math.sin(wavePosition * 0.1)
  }
}

/**
 * Color utilities for neural visualization
 */
export class ColorUtils {
  /**
   * Create color with dramatic activation scaling
   */
  static createActivationColor(activation: number, baseColor: number): any {
    // This will be filled in when THREE is available
    return null
  }

  /**
   * Interpolate between two colors based on activation
   */
  static lerpColors(color1: any, color2: any, factor: number): any {
    // This will be filled in when THREE is available
    return null
  }
}
